<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useAuthStore } from './stores/auth'
import { useRouter, useRoute } from 'vue-router'
import afabLogo from './assets/afab-logo.png'
import NavDropdown from './components/NavDropdown.vue'

const auth = useAuthStore()
const router = useRouter()
const route = useRoute()

// Build-time flag (default OFF → hidden in production unless the env sets it).
const fuelDashboardEnabled = import.meta.env.VITE_FEATURE_FUEL_DASHBOARD === 'true'

const mobileMenuOpen = ref(false)

// Help is for the Afab roles that handle invoices and costs; matches the route's gate.
const showHelp = computed(() => auth.isDispatcher || auth.isMaintenance || auth.isAccounting)

watch(() => route.fullPath, () => {
  mobileMenuOpen.value = false
})

async function handleLogout() {
  mobileMenuOpen.value = false
  await auth.logout()
  router.push('/login')
}
</script>

<template>
  <div class="min-h-screen bg-gray-50 flex flex-col">
    <!-- Nav bar -->
    <nav v-if="auth.isLoggedIn" class="bg-white border-b border-gray-200 shadow-sm w-full max-w-full">
      <div class="max-w-7xl mx-auto px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between gap-2">
        <!-- Left: logo + desktop links -->
        <div class="flex items-center gap-3 md:gap-6 min-w-0">
          <router-link :to="auth.homeRoute" class="flex items-center shrink-0" aria-label="AFAB home">
            <img :src="afabLogo" alt="AFAB" class="h-7 sm:h-9 w-auto" />
          </router-link>

          <div class="hidden md:flex items-center gap-6">
            <template v-if="auth.isDispatcher">
              <NavDropdown label="Orders">
                <router-link to="/admin" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Orders</router-link>
                <router-link to="/admin/orders/new" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">New Order</router-link>
                <router-link to="/admin/dispatch" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Dispatch (both)</router-link>
                <router-link to="/admin/long-range" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Long-Range</router-link>
                <router-link v-if="auth.isAdmin" to="/admin/stats" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Stats</router-link>
              </NavDropdown>
              <NavDropdown label="Fleet">
                <router-link to="/admin/fleet" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Fleet</router-link>
                <router-link to="/admin/locations" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Freight Locations</router-link>
                <router-link v-if="auth.isAdmin && fuelDashboardEnabled" to="/accounting/fuel" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Fuel</router-link>
              </NavDropdown>
            </template>
            <template v-if="auth.isAdmin">
              <NavDropdown label="Clients">
                <router-link to="/admin/clients" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Clients</router-link>
                <router-link to="/admin/invites" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Invites</router-link>
              </NavDropdown>
            </template>
            <template v-if="auth.isAccounting">
              <router-link to="/accounting" class="text-sm text-gray-600 hover:text-teal-600">Reports</router-link>
              <router-link v-if="fuelDashboardEnabled" to="/accounting/fuel" class="text-sm text-gray-600 hover:text-teal-600">Fuel</router-link>
            </template>
            <template v-if="auth.isClient">
              <router-link to="/client" class="text-sm text-gray-600 hover:text-teal-600">Orders</router-link>
              <router-link to="/client/locations" class="text-sm text-gray-600 hover:text-teal-600">Locations</router-link>
              <template v-if="auth.isClientManager">
                <router-link to="/client/orders/new" class="text-sm text-gray-600 hover:text-teal-600">New Order</router-link>
                <router-link to="/client/users" class="text-sm text-gray-600 hover:text-teal-600">Team</router-link>
              </template>
            </template>
            <template v-if="auth.isDriver">
              <router-link to="/driver" class="text-sm text-gray-600 hover:text-teal-600">Orders</router-link>
              <router-link v-if="auth.isLongHaulQualified" to="/driver/long-range" class="text-sm text-gray-600 hover:text-teal-600">Long-Range</router-link>
            </template>
            <!-- HOP-130: the shop gets one destination. Flat link rather than the
                 dispatcher's Fleet dropdown, which also carries Freight Locations
                 and Fuel — neither of which this role can open. -->
            <template v-if="auth.isMaintenance">
              <router-link to="/admin/fleet" class="text-sm text-gray-600 hover:text-teal-600">Fleet</router-link>
            </template>
          </div>
        </div>

        <!-- Right: desktop user menu / mobile hamburger -->
        <div class="hidden md:flex items-center gap-4">
          <NavDropdown align="right">
            <template #trigger>
              <span class="text-sm text-gray-700">{{ auth.profile?.full_name }}</span>
            </template>
            <div class="px-4 py-2 border-b border-gray-100">
              <p class="text-sm font-medium text-gray-800">{{ auth.profile?.full_name }}</p>
              <p class="text-xs text-gray-500 capitalize">{{ auth.role?.replace('_', ' ') }}</p>
            </div>
            <router-link v-if="showHelp" to="/admin/help" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Help</router-link>
            <router-link to="/change-password" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Change Password</router-link>
            <button @click="handleLogout" class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Logout</button>
          </NavDropdown>
        </div>

        <button
          @click="mobileMenuOpen = !mobileMenuOpen"
          class="md:hidden p-1.5 text-gray-600 hover:text-gray-900 shrink-0"
          :aria-expanded="mobileMenuOpen"
          aria-label="Menu">
          <svg v-if="!mobileMenuOpen" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-6 h-6" aria-hidden="true">
            <path fill-rule="evenodd" d="M2 5.75A.75.75 0 0 1 2.75 5h14.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 5.75zm0 4.25A.75.75 0 0 1 2.75 9.25h14.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 10zm.75 3.5a.75.75 0 0 0 0 1.5h14.5a.75.75 0 0 0 0-1.5H2.75z" clip-rule="evenodd"/>
          </svg>
          <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-6 h-6" aria-hidden="true">
            <path fill-rule="evenodd" d="M4.28 3.22a.75.75 0 0 0-1.06 1.06L8.94 10l-5.72 5.72a.75.75 0 1 0 1.06 1.06L10 11.06l5.72 5.72a.75.75 0 1 0 1.06-1.06L11.06 10l5.72-5.72a.75.75 0 0 0-1.06-1.06L10 8.94 4.28 3.22z" clip-rule="evenodd"/>
          </svg>
        </button>
      </div>

      <!-- Mobile menu -->
      <div v-if="mobileMenuOpen" class="md:hidden border-t border-gray-200 bg-white">
        <nav class="px-2 py-2 flex flex-col">
          <template v-if="auth.isDispatcher">
            <p class="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Orders</p>
            <router-link to="/admin" class="px-3 py-2 rounded text-sm text-gray-700 hover:bg-gray-100">Orders</router-link>
            <router-link to="/admin/orders/new" class="px-3 py-2 rounded text-sm text-gray-700 hover:bg-gray-100">New Order</router-link>
            <router-link to="/admin/dispatch" class="px-3 py-2 rounded text-sm text-gray-700 hover:bg-gray-100">Dispatch (both)</router-link>
            <router-link to="/admin/long-range" class="px-3 py-2 rounded text-sm text-gray-700 hover:bg-gray-100">Long-Range</router-link>
            <router-link v-if="auth.isAdmin" to="/admin/stats" class="px-3 py-2 rounded text-sm text-gray-700 hover:bg-gray-100">Stats</router-link>
            <p class="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Fleet</p>
            <router-link to="/admin/fleet" class="px-3 py-2 rounded text-sm text-gray-700 hover:bg-gray-100">Fleet</router-link>
            <router-link to="/admin/locations" class="px-3 py-2 rounded text-sm text-gray-700 hover:bg-gray-100">Freight Locations</router-link>
            <router-link v-if="auth.isAdmin && fuelDashboardEnabled" to="/accounting/fuel" class="px-3 py-2 rounded text-sm text-gray-700 hover:bg-gray-100">Fuel</router-link>
          </template>
          <template v-if="auth.isAdmin">
            <p class="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Clients</p>
            <router-link to="/admin/clients" class="px-3 py-2 rounded text-sm text-gray-700 hover:bg-gray-100">Clients</router-link>
            <router-link to="/admin/invites" class="px-3 py-2 rounded text-sm text-gray-700 hover:bg-gray-100">Invites</router-link>
          </template>
          <template v-if="auth.isAccounting">
            <router-link to="/accounting" class="px-3 py-2 rounded text-sm text-gray-700 hover:bg-gray-100">Reports</router-link>
            <router-link v-if="fuelDashboardEnabled" to="/accounting/fuel" class="px-3 py-2 rounded text-sm text-gray-700 hover:bg-gray-100">Fuel</router-link>
          </template>
          <template v-if="auth.isClient">
            <router-link to="/client" class="px-3 py-2 rounded text-sm text-gray-700 hover:bg-gray-100">Orders</router-link>
            <router-link to="/client/locations" class="px-3 py-2 rounded text-sm text-gray-700 hover:bg-gray-100">Locations</router-link>
            <template v-if="auth.isClientManager">
              <router-link to="/client/orders/new" class="px-3 py-2 rounded text-sm text-gray-700 hover:bg-gray-100">New Order</router-link>
              <router-link to="/client/users" class="px-3 py-2 rounded text-sm text-gray-700 hover:bg-gray-100">Team</router-link>
            </template>
          </template>
          <template v-if="auth.isDriver">
            <router-link to="/driver" class="px-3 py-2 rounded text-sm text-gray-700 hover:bg-gray-100">Orders</router-link>
            <router-link v-if="auth.isLongHaulQualified" to="/driver/long-range" class="px-3 py-2 rounded text-sm text-gray-700 hover:bg-gray-100">Long-Range</router-link>
          </template>
          <template v-if="auth.isMaintenance">
            <router-link to="/admin/fleet" class="px-3 py-2 rounded text-sm text-gray-700 hover:bg-gray-100">Fleet</router-link>
          </template>

          <!-- Account -->
          <div class="mt-2 pt-2 border-t border-gray-100">
            <div class="px-3 py-1">
              <p class="text-sm font-medium text-gray-800">{{ auth.profile?.full_name }}</p>
              <p class="text-xs text-gray-500 capitalize">{{ auth.role?.replace('_', ' ') }}</p>
            </div>
            <router-link v-if="showHelp" to="/admin/help" class="block px-3 py-2 rounded text-sm text-gray-700 hover:bg-gray-100">Help</router-link>
            <router-link to="/change-password" class="block px-3 py-2 rounded text-sm text-gray-700 hover:bg-gray-100">Change Password</router-link>
            <button @click="handleLogout" class="w-full text-left px-3 py-2 rounded text-sm text-gray-700 hover:bg-gray-100">
              Logout
            </button>
          </div>
        </nav>
      </div>
    </nav>

    <main class="flex-1">
      <router-view />
    </main>

    <!-- Footer with legal links — visible at every route, signed in or out. -->
    <footer class="border-t border-gray-200 bg-white mt-8 sm:mt-12">
      <div class="max-w-7xl mx-auto px-4 py-2 sm:py-4 flex flex-wrap items-center justify-between gap-2 text-[11px] sm:text-xs text-gray-500">
        <span>&copy; {{ new Date().getFullYear() }} AFAB</span>
        <span class="space-x-3 sm:space-x-4">
          <router-link to="/terms" class="hover:text-gray-700 hover:underline">Terms</router-link>
          <router-link to="/privacy" class="hover:text-gray-700 hover:underline">Privacy</router-link>
        </span>
      </div>
    </footer>
  </div>
</template>
